import React, { useEffect, useState } from "react";
import adminService from "@/services/api/admin.service";
import { notificationService } from "../../../services/api/notification.service";
import { Loader } from "lucide-react"; // At the top, before your component:
const READ_APPOINTMENTS_KEY = "readAppointmentIds";
import { formatInTimeZone } from "date-fns-tz";

const AppointmentList = () => {
  const [groupedAppointments, setGroupedAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [totalPages, setTotalPages] = useState(1);
  const [markingAsRead, setMarkingAsRead] = useState(false);
  // In your component:
  const [readAppointmentIds, setReadAppointmentIds] = useState(() => {
    // Load from localStorage on mount
    const stored = localStorage.getItem(READ_APPOINTMENTS_KEY);
    return stored ? JSON.parse(stored) : [];
  });

  useEffect(() => {
    fetchAppointments();
  }, [currentPage]);

  const fetchAppointments = async () => {
    try {
      const response = await adminService.listAppointments({
        page: currentPage,
      });

      // Group appointments by expert
      const grouped = response.appointments.reduce((acc, appointment) => {
        const expertId = appointment.expertDetails
          ? `${appointment.expertDetails.firstName}-${appointment.expertDetails.lastName}`
          : "unknown";

        if (!acc[expertId]) {
          acc[expertId] = {
            expertName: appointment.expertDetails
              ? `${appointment.expertDetails.firstName} ${appointment.expertDetails.lastName}`
              : "Unknown Expert",
            specialties: appointment.expertDetails?.specialties || [],
            appointments: [],
          };
        }
        acc[expertId].appointments.push(appointment);
        return acc;
      }, {});

      setGroupedAppointments(Object.values(grouped));
      setTotalPages(Math.ceil(response.total / 10)); // Assuming 10 items per page
      setLoading(false);
    } catch (err) {
      console.error("Error fetching appointments:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  // When marking as read:
  const handleMarkAsRead = async () => {
    try {
      setMarkingAsRead(true);
      const response = await notificationService.markCategoryAsRead(
        "Appointment"
      );
      if (response.success) {
        const newIds = groupedAppointments
          .flatMap((expert) => expert.appointments)
          .filter((appointment) => isNewAppointment(appointment.createdAt))
          .map((appointment) => appointment._id);
        const updatedIds = Array.from(
          new Set([...readAppointmentIds, ...newIds])
        );
        setReadAppointmentIds(updatedIds);
        localStorage.setItem(READ_APPOINTMENTS_KEY, JSON.stringify(updatedIds));
        await fetchAppointments();
      } else {
        setError("Failed to mark notifications as read");
      }
    } catch (error) {
      setError("Failed to mark notifications as read");
    } finally {
      setMarkingAsRead(false);
    }
  };

  const formatDate = (dateString) => {
    return formatInTimeZone(dateString, "UTC", "MMM d, yyyy 'at' h:mm a");
  };
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "scheduled":
        return "bg-blue-100 text-blue-800";
      case "rescheduled":
        return "bg-purple-100 text-purple-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Function to check if appointment is new (created within last 24 hours)
  const isNewAppointment = (createdAt) => {
    if (!createdAt) return false;
    const appointmentDate = new Date(createdAt);
    const now = new Date();
    const timeDifference = now - appointmentDate;
    const hoursDifference = timeDifference / (1000 * 60 * 60);
    return hoursDifference <= 24; // Consider appointments created within 24 hours as "new"
  };

  // Check if there are any new appointments
  const hasNewAppointments = groupedAppointments.some((expert) =>
    expert.appointments.some((appointment) =>
      isNewAppointment(appointment.createdAt)
    )
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin text-indigo-500 mb-4" size={36} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-lg max-w-md mx-auto text-center">
        <h3 className="text-lg font-medium text-red-800">Error</h3>
        <p className="text-red-600 mt-2">{error}</p>
      </div>
    );
  }

  return (
    <div className="p-6 rounded-lg">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">
          Appointments by Expert
        </h1>
        {hasNewAppointments && (
          <button
            onClick={handleMarkAsRead}
            disabled={markingAsRead}
            className="self-end sm:self-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {markingAsRead ? (
              <>
                <Loader className="animate-spin" size={16} />
                Marking as Read...
              </>
            ) : (
              "Mark All as Read"
            )}
          </button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-200 shadow-md rounded-lg">
          <thead className="bg-gray-100">
            <tr>
              <th className="py-3 px-4 text-left text-sm font-medium text-gray-600">
                Expert
              </th>
              <th className="py-3 px-4 text-left text-sm font-medium text-gray-600 w-32">
                Specialties
              </th>
              <th className="py-3 px-4 text-left text-sm font-medium text-gray-600">
                Appointments
              </th>
              <th className="py-3 px-4 text-left text-sm font-medium text-gray-600">
                User Details
              </th>
              <th className="py-3 px-4 text-left text-sm font-medium text-gray-600">
                Details
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {groupedAppointments.map((expert, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="py-4 px-4">
                  <div className="flex items-center gap-3">
                    <div>
                      <div className="font-medium text-gray-900">
                        {expert.expertName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {expert.appointments.length} appointments
                      </div>
                    </div>
                    {/* Blue dot indicator if expert has any new appointments */}
                  </div>
                </td>

                <td className="py-4 px-4 w-32">
                  <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                    {expert.specialties.map((specialty, i) => (
                      <span
                        key={i}
                        className="px-2 py-1 bg-gray-200 text-gray-800 text-xs rounded-full whitespace-nowrap"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                </td>

                <td className="py-4 px-4">
                  <div className="space-y-2">
                    {expert.appointments.map((appointment) => (
                      <div
                        key={appointment._id}
                        className="flex items-center gap-2"
                      >
                        <span className="ml-2 text-sm font-medium">
                          {formatDate(appointment.scheduledAt)}
                        </span>
                        {isNewAppointment(appointment.createdAt) &&
                          !readAppointmentIds.includes(appointment._id) && (
                            <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                          )}
                      </div>
                    ))}
                  </div>
                </td>
                <td className="py-4 px-4">
                  <div className="space-y-3">
                    {expert.appointments.map((appointment) => (
                      <div
                        key={appointment._id}
                        className="border-l-2 pl-3 border-gray-200"
                      >
                        <div className="text-sm">
                          <span className="font-medium">Name:</span>{" "}
                          {appointment.userDetails?.fullName || "N/A"}
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">Email:</span>{" "}
                          {appointment.userDetails?.email || "N/A"}
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">Mobile:</span>{" "}
                          {appointment.userDetails?.mobileNo || "N/A"}
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">Status:</span>{" "}
                          {appointment.userDetails?.status || "N/A"}
                        </div>
                      </div>
                    ))}
                  </div>
                </td>

                <td className="py-4 px-4">
                  <div className="space-y-3">
                    {expert.appointments.map((appointment) => (
                      <div
                        key={appointment._id}
                        className="border-l-2 pl-3 border-gray-200"
                      >
                        <div className="flex justify-between text-sm">
                          <span className="font-medium">Status:</span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(
                              appointment.status
                            )}`}
                          >
                            {appointment.status}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="font-medium">Duration:</span>
                          <span>{appointment.duration} mins</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="font-medium">Type:</span>
                          <span className="capitalize">
                            {appointment.meetingType}
                          </span>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">Topic:</span>{" "}
                          {appointment.consultationTopic}
                        </div>
                      </div>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center mt-8">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 mx-1 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50"
          >
            Previous
          </button>
          {[...Array(totalPages).keys()].map((_, index) => (
            <button
              key={index + 1}
              onClick={() => setCurrentPage(index + 1)}
              className={`px-4 py-2 mx-1 rounded-md ${
                currentPage === index + 1
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              {index + 1}
            </button>
          ))}
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className="px-4 py-2 mx-1 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AppointmentList;
